import { axiosWithoutToken } from "@/utils/axios";

export type CurrencyCode = "NGN" | "USD" | "GBP" | "CAD" | "EUR" | "CNY";

export interface ExchangeRateResponse {
  success: boolean;
  base: string;
  targets: string[];
  rates: Record<string, number>;
  last_updated?: string;
  error?: {
    code?: string | number;
    type?: string;
    info?: string;
  };
}

/** Base currency the frontend fetches all other rates relative to. */
export const BASE_CURRENCY: CurrencyCode = "USD";
/** Target currencies fetched alongside the base in a single request. */
export const DEFAULT_TARGETS: CurrencyCode[] = [
  "NGN",
  "GBP",
  "CAD",
  "EUR",
  "CNY",
];
/** All currencies the frontend can display. */
export const SUPPORTED_CURRENCIES: CurrencyCode[] = [
  "NGN",
  "USD",
  "GBP",
  "CAD",
  "EUR",
  "CNY",
];

/**
 * Fetches the latest exchange rates from our backend `/exchange-rates` endpoint
 * (which proxies currencylayer.com). The backend returns a `rates` map in which
 * the base currency equals 1 and every requested target currency has its own
 * rate (e.g. `{ USD: 1, NGN: 1580.25, GBP: 0.8 }`).
 *
 * A single request fetches the base plus all supported targets. If the backend
 * omits a target (e.g. GBP not yet available), that target is skipped rather
 * than failing the whole fetch, so currencies the backend does support keep
 * converting while the missing ones gracefully fall back to the original amount.
 *
 * @returns A map of currency code -> rate, relative to the base.
 */
export class CurrencyConverterService {
  static async getExchangeRates(
    base: CurrencyCode = BASE_CURRENCY,
    targets: CurrencyCode[] = DEFAULT_TARGETS,
  ): Promise<Record<string, number>> {
    const params: Record<string, string> = {
      base,
      targets: targets.join(","),
    };

    try {
      const { data } = await axiosWithoutToken.get<ExchangeRateResponse>(
        "/exchange-rates",
        { params, timeout: 8000 },
      );

      if (!data || !data.success || data.error) {
        const message =
          typeof data?.error?.info === "string"
            ? data.error.info
            : "Failed to fetch exchange rates";
        throw new Error(message);
      }

      const baseKey = (data.base || base).toUpperCase();
      const sourceRates = data.rates ?? {};
      const result: Record<string, number> = {};

      let baseRate = sourceRates[baseKey];
      if (!Number.isFinite(baseRate)) {
        baseRate = sourceRates[baseKey.toLowerCase()];
      }
      if (Number.isFinite(baseRate) && baseRate > 0) {
        result[baseKey] = baseRate;
      }

      for (const key of targets.map((t) => t.toUpperCase())) {
        let rate = sourceRates[key];

        if (!Number.isFinite(rate)) {
          const lowerKey = key.toLowerCase();
          rate = sourceRates[lowerKey];
        }

        if (!Number.isFinite(rate)) {
          const matchingKey = Object.keys(sourceRates).find((k) =>
            k.endsWith(key),
          );
          if (matchingKey) {
            rate = sourceRates[matchingKey];
          }
        }

        if (Number.isFinite(rate) && rate > 0) {
          result[key] = rate;
        } else {
          console.warn(
            `[currencyConvert] Missing or invalid rate for target "${key}" in response. Available keys: ${Object.keys(sourceRates).join(", ")}`,
          );
        }
      }

      console.log("[currencyConvert] Fetched exchange rates:", result);
      return result;
    } catch (error: any) {
      const message =
        error?.response?.data?.error?.info ||
        error?.message ||
        "Unknown currency API error";
      console.error(
        "[currencyConvert] Failed to fetch exchange rates:",
        message,
      );
      throw new Error(message);
    }
  }

  /**
   * Builds NGN-based rates (each value = amount of that currency worth 1 NGN)
   * from a single `base=USD` request that targets every other supported
   * currency. This avoids issuing one request per currency as base — several
   * backend bases (e.g. CNY) are not supported, and any single failure used to
   * abort the whole refresh.
   *
   * The backend returns USD-based rates (`1 USD = X <currency>`), so the
   * NGN-based rate for a currency is `rate[currency] / rate.NGN`.
   *
   * Result example: `{ NGN: 1, USD: 0.0007, GBP: 0.00055, CAD: 0.001, EUR: 0.00065, CNY: 0.005 }`
   */
  static async getNgnBaseRates(): Promise<Record<string, number>> {
    const base: CurrencyCode = BASE_CURRENCY; // USD
    const targets = SUPPORTED_CURRENCIES.filter((code) => code !== base);

    const usdRates = await this.getExchangeRates(base, targets);

    const ngnPerUsd = usdRates.NGN;

    const merged: Record<string, number> = {
      NGN: 1,
      USD: Number.isFinite(ngnPerUsd) && ngnPerUsd > 0 ? 1 / ngnPerUsd : 0,
    };

    for (const code of targets) {
      if (code === "NGN") continue;
      const perUsd = usdRates[code];
      merged[code] =
        Number.isFinite(perUsd) &&
        perUsd > 0 &&
        Number.isFinite(ngnPerUsd) &&
        ngnPerUsd > 0
          ? perUsd / ngnPerUsd
          : 0;
    }

    console.log("[currencyConvert] NGN-base rates:", merged);
    return merged;
  }

  /**
   * Convert `amount` from `fromCurrency` to `toCurrency` using the supplied
   * `rates` map. Rates are expected to be NGN-based, meaning each value is
   * the amount of that currency equal to 1 NGN.
   *
   * Formula:
   * - from == NGN => amount * rates[to]
   * - to   == NGN => amount / rates[from]
   * - otherwise   => (amount / rates[from]) * rates[to]
   *
   * Any missing/invalid rate falls back to the original amount.
   */
  static convert(
    amount: number,
    fromCurrency: string,
    toCurrency: string,
    rates: Record<string, number>,
  ): number {
    if (!Number.isFinite(amount)) return 0;

    const from = (fromCurrency || "NGN").toUpperCase();
    const to = (toCurrency || "NGN").toUpperCase();

    if (!rates || Object.keys(rates).length === 0) return amount;
    if (from === to) return amount;

    const fromRate = rates[from];
    const toRate = rates[to];

    if (!Number.isFinite(fromRate) || fromRate <= 0) {
      console.warn(
        `[currencyConvert] Invalid fromRate for "${from}" while converting ${amount} ${from} → ${to}`,
        rates,
      );
      return amount;
    }
    if (!Number.isFinite(toRate) || toRate <= 0) {
      console.warn(
        `[currencyConvert] Invalid toRate for "${to}" while converting ${amount} ${from} → ${to}`,
        rates,
      );
      return amount;
    }

    if (from === "NGN") {
      return amount * toRate;
    }

    if (to === "NGN") {
      return amount / fromRate;
    }

    return (amount / fromRate) * toRate;
  }
}
